import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { PublicFullReport } from '@/entities/publicReport';

import {
    chapterVisible,
    headingMatches,
    indexFullReport,
    matchesFilter,
    type FindingFilter,
    type FullReportIndex,
    type IndexedEntry,
} from './reportIndex';

/** Waar een sprong heen gaat: een hoofdstuk, een kop, of een bevinding daarin. */
export interface RevealTarget {
    chapter: string;
    /** Het `id` in de pagina. */
    anchor: string;
    /** Een hoofdstuk of kop: de focus gaat naar de kop zelf. */
    heading: boolean;
    /** Telt op, zodat twee sprongen naar hetzelfde doel allebei gebeuren. */
    serial: number;
}

export interface FullReportState {
    index: FullReportIndex;
    filter: FindingFilter;
    /** Een filter zet de hoofdstukken met iets erin open, zodat de uitkomst meteen in beeld is. */
    setFilter: (filter: FindingFilter) => void;
    isOpen: (key: string) => boolean;
    toggle: (key: string) => void;
    /** Of elk hoofdstuk dat nu in beeld is, open staat. */
    allOpen: boolean;
    setAllOpen: (open: boolean) => void;
    /** Een sprong die nog moet gebeuren zodra het rapport in beeld is. */
    target: RevealTarget | null;
    /**
     * Naar een hoofdstuk of een kop erin (`6-2`): het hoofdstuk gaat open, en een
     * filter dat de plek wegzet, gaat terug naar Alles. Met `navigating` zet de
     * aanroeper de plek zelf in de adresbalk; komt hij daar aan, dan is de sprong
     * al gedaan. Geeft `false` voor een plek die het rapport niet heeft.
     */
    revealTarget: (key: string, options?: { navigating?: boolean }) => boolean;
    /** Naar een bevinding; in de adresbalk komt de kop waar hij onder staat. */
    revealEntry: (indexed: IndexedEntry, options?: { navigating?: boolean }) => void;
    /** De sprong is gedaan. */
    revealed: () => void;
}

const EMPTY_REPORT: PublicFullReport = {
    cover: {
        kind: '',
        norm: null,
        street: '',
        place: '',
        inspectedAt: null,
        inspector: null,
        orderNumber: '',
        version: 1,
        generatedAt: null,
    },
    chapters: [],
};

/**
 * Wat de koper in het tabblad Rapport dichtklapte en koos: het filter, de
 * dichte hoofdstukken en een sprong die nog moet gebeuren. Het leeft boven de
 * tabbladen, zodat het blijft staan als de koper even naar een ander tabblad
 * gaat, en zodat een regel in het overzicht of de kosten naar een plek in het
 * rapport kan wijzen.
 *
 * Elk hoofdstuk staat open, zoals in een document; de koper kan er een
 * dichtklappen.
 *
 * De plek in de adresbalk (`#rapport-6-2`) wordt hier gevolgd, ook als een
 * ander tabblad open is: een link of Terug naar een plek springt daarheen, één
 * keer per verandering, en een sprong vanaf de pagina zelf die de plek in de
 * adresbalk zet, gebeurt niet nog eens.
 */
export function useFullReportState(
    fullReport: PublicFullReport | null,
    urlTarget: string | null,
): FullReportState {
    const report = fullReport ?? EMPTY_REPORT;
    const index = useMemo(() => indexFullReport(report), [report]);
    const [filter, setFilterState] = useState<FindingFilter>('alles');
    const [closed, setClosed] = useState<ReadonlySet<string>>(() => new Set());
    const [target, setTarget] = useState<RevealTarget | null>(null);
    /* De plek die een sprong vanaf de pagina zelf in de adresbalk zet. */
    const expectedTarget = useRef<string | null>(null);
    const currentUrlTarget = useRef(urlTarget);

    useEffect(() => {
        currentUrlTarget.current = urlTarget;
    }, [urlTarget]);

    /* De hoofdstukken van het tabblad Rapport die onder dit filter in beeld zijn; de NHG-bijlage heeft een eigen tabblad. */
    const visibleKeys = useMemo(
        () =>
            report.chapters
                .filter(
                    (chapter) => chapter.kind !== 'nhg' && chapterVisible(chapter, index, filter),
                )
                .map((chapter) => chapter.key),
        [report, index, filter],
    );

    const setFilter = useCallback(
        (next: FindingFilter) => {
            setFilterState(next);

            if (next === 'alles') {
                return;
            }

            const withResults = report.chapters
                .filter((chapter) => chapterVisible(chapter, index, next))
                .map((chapter) => chapter.key);

            setClosed((current) => without(current, withResults));
        },
        [report, index],
    );

    const toggle = useCallback((key: string) => {
        setClosed((current) => {
            const next = new Set(current);

            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }

            return next;
        });
    }, []);

    const setAllOpen = useCallback(
        (value: boolean) => {
            setClosed((current) =>
                value ? without(current, visibleKeys) : new Set([...current, ...visibleKeys]),
            );
        },
        [visibleKeys],
    );

    const reveal = useCallback(
        (
            chapter: string,
            anchor: string,
            options: { heading: boolean; hidden: boolean; urlKey: string; navigating: boolean },
        ) => {
            if (options.hidden) {
                setFilterState('alles');
            }

            /* Staat de plek al in de adresbalk, dan komt er geen verandering om op te wachten. */
            if (options.navigating && options.urlKey !== currentUrlTarget.current) {
                expectedTarget.current = options.urlKey;
            }

            setClosed((current) => (current.has(chapter) ? without(current, [chapter]) : current));
            setTarget((current) => ({
                chapter,
                anchor,
                heading: options.heading,
                serial: (current?.serial ?? 0) + 1,
            }));
        },
        [],
    );

    const revealTarget = useCallback(
        (key: string, options: { navigating?: boolean } = {}) => {
            const place = index.targets.get(key);

            if (!place) {
                return false;
            }

            const hidden =
                place.heading === null
                    ? !visibleKeys.includes(place.chapterKey)
                    : filter !== 'alles' && !headingMatches(place.heading, index, filter);

            reveal(place.chapterKey, place.anchor, {
                heading: true,
                hidden,
                urlKey: key,
                navigating: options.navigating === true,
            });

            return true;
        },
        [index, visibleKeys, filter, reveal],
    );

    const revealEntry = useCallback(
        (indexed: IndexedEntry, options: { navigating?: boolean } = {}) => {
            reveal(indexed.chapterKey, indexed.anchor, {
                heading: false,
                hidden: !matchesFilter(indexed, filter),
                urlKey: indexed.targetKey,
                navigating: options.navigating === true,
            });
        },
        [filter, reveal],
    );

    const revealed = useCallback(() => setTarget(null), []);

    /* Een link naar een plek, of Terug in de browser: daarheen, één keer per verandering. */
    const seenTarget = useRef<string | null>(null);

    useEffect(() => {
        if (urlTarget === seenTarget.current) {
            return;
        }

        seenTarget.current = urlTarget;

        const expected = expectedTarget.current;

        expectedTarget.current = null;

        if (urlTarget !== null && expected !== urlTarget) {
            revealTarget(urlTarget);
        }
    }, [urlTarget, revealTarget]);

    return {
        index,
        filter,
        setFilter,
        isOpen: (key) => !closed.has(key),
        toggle,
        allOpen: visibleKeys.length > 0 && visibleKeys.every((key) => !closed.has(key)),
        setAllOpen,
        target,
        revealTarget,
        revealEntry,
        revealed,
    };
}

function without(set: ReadonlySet<string>, keys: readonly string[]): ReadonlySet<string> {
    if (!keys.some((key) => set.has(key))) {
        return set;
    }

    const next = new Set(set);

    for (const key of keys) {
        next.delete(key);
    }

    return next;
}
